import { useAppSelector } from "@app/store";
import Copy from "@assets/icons/copy.svg";
import { Button } from "@components/button";
import { errorMessage } from "@components/forms/error";
import { BaseInput, Input } from "@components/input";
import { FlexLoader, GlobalLoader } from "@components/loader";
import { Modal } from "@components/modal";
import { useDevicesDispatch } from "@features/device";
import { CreateQRDeviceResponse } from "@features/device/api";
import { copyToClipboard, withErrorHandling } from "@helpers/utilities";
import { yupResolver } from "@hookform/resolvers/yup";
import { memo, useCallback, useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import QRCode from "react-qr-code";
import * as yup from "yup";
import styles from "./devices.module.scss";

type Props = {
  isOpen: boolean;
  hide: () => void;
};

export const AddDeviceModal = memo(({ isOpen, hide }: Props) => {
  return (
    <Modal
      className={styles.modal}
      isOpen={isOpen}
      onClose={hide}
      title="Добавить новое устройство"
    >
      <Content hide={hide} />
    </Modal>
  );
});

type ContentProps = Pick<Props, "hide">;

const schema = yup.object().shape({
  name: yup.string().required(errorMessage.required).trim(),
});

type FormData = { name: string };

function Content({ hide }: ContentProps) {
  const { createQRDevice, updateDevice, getDevices } = useDevicesDispatch();
  const isUpdating = useAppSelector((state) => state.device.isUpdating);

  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState("");
  const [qrDevice, setQRDevice] = useState<CreateQRDeviceResponse>();

  useEffect(() => {
    withErrorHandling(
      async () => {
        const res = await createQRDevice();

        setQRDevice(res);
      },
      setError,
      () => setIsLoading(false)
    );
  }, [createQRDevice]);

  const { control, handleSubmit } = useForm<FormData>({
    resolver: yupResolver(schema),
  });

  const onSubmit = useCallback(
    async (data: FormData) => {
      if (qrDevice?.device_id) {
        await updateDevice({ deviceId: qrDevice.device_id, name: data.name });
        getDevices({ limit: 10, page: 1 });
        hide();
      }
    },
    [hide, qrDevice?.device_id, updateDevice]
  );

  if (isLoading) {
    return <FlexLoader />;
  }

  if (!qrDevice || error) {
    return <span>{error || "Произошла ошибка"}</span>;
  }

  return (
    <div className={styles.modalBody}>
      <Input
        name="name"
        control={control}
        label="Имя устройства"
        className={styles.input}
        placeholder="Введите имя"
      />
      <BaseInput
        className={styles.input}
        value={qrDevice.qr_code}
        icon={<Copy />}
        onClick={() => {
          copyToClipboard(qrDevice.qr_code);
        }}
      />

      <QRCode value={qrDevice.qr_code} />
      <Button className={styles.button} onClick={handleSubmit(onSubmit)}>
        Готово
      </Button>
      {isUpdating && <GlobalLoader />}
    </div>
  );
}
